import React, { useEffect, useRef, useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../utils/cropImage';

const ImageCrop = ({ 
  image, 
  onCropComplete, 
  onCancel, 
  onClose,
  aspectRatio = 1,
  cropShape = 'round',
  showGrid = false
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const onCropChange = useCallback((crop) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom) => {
    setZoom(zoom);
  }, []);

  const onCropCompleteHandler = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropComplete = useCallback(async () => {
    try {
      if (isSubmitting) return;
      if (!image || !croppedAreaPixels) return;

      setIsSubmitting(true);

      if (onClose) {
        onClose();
      }
      
      const croppedImage = await getCroppedImg(
        image,
        croppedAreaPixels,
        0 // rotation
      );
      await onCropComplete(croppedImage);
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  }, [croppedAreaPixels, image, isSubmitting, onClose, onCropComplete]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
      <div className="bg-white rounded-2xl p-4 max-w-lg w-full mx-4">
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Crop Profile Picture</h3>
          <p className="text-gray-600 text-xs">Adjust the image to fit perfectly in your profile picture</p>
        </div>

        {/* Cropper Container */}
        <div className="relative mb-3" style={{ height: '300px', backgroundColor: '#f3f4f6' }}>
          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            cropShape={cropShape}
            showGrid={showGrid}
            onCropChange={onCropChange}
            onCropComplete={onCropCompleteHandler}
            onZoomChange={onZoomChange}
            style={{
              containerStyle: {
                width: '100%',
                height: '100%',
                backgroundColor: '#f3f4f6'
              },
              mediaStyle: {
                maxHeight: '100%',
                maxWidth: '100%'
              }
            }}
          />
        </div>

        {/* Zoom Slider */}
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Zoom: {Math.round(zoom * 100)}%
          </label>
          <input
            type="range"
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-romantic-500"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-2">
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleCropComplete}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm bg-romantic-500 text-white rounded-lg font-medium hover:bg-romantic-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Crop & Upload
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCrop;
